import { useState } from "react";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import { API_URL, authHeaders, formatData, statoBadgeClass } from "./comuni";
import { riaperturaNonRichiusa } from "./percorsiCondivisi";

/**
 * Conferma del pulsante "Chiudi" su un percorso da chiudere (avviso dello Storico, task 517).
 * Chiama POST /gestione-percorsi/percorsi/:id/chiudi, che esegue su Interacta le transizioni
 * del workflow che l'operatore non ha fatto (non annullabili).
 *
 * Lo stato finale mostrato qui è quello PROPOSTO dall'elenco: il backend lo ricalcola al
 * momento del click dalle tratte fatte, quindi se nel frattempo l'autista ne ha segnata
 * un'altra, vince il backend (la risposta dice lo stato reale, che il padre mostra).
 * Errori del backend in chiaro: 409 già chiuso da altri, 503 tratte non leggibili,
 * 502 Interacta ha rifiutato.
 *
 * Props: percorso (riga di GET /percorsi/da-chiudere), onClose, onChiuso(risposta),
 * top (true se aperto sopra una tabella in fullscreen: alza lo z-index dell'overlay).
 */
export default function ModaleChiusuraPercorso({ percorso: p, onClose, onChiuso, top = false }) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const conferma = async () => {
        setSaving(true);
        setError("");
        try {
            const res = await axios.post(`${API_URL}/gestione-percorsi/percorsi/${p.id}/chiudi`, {}, { headers: authHeaders() });
            onChiuso(res.data);
        } catch (err) {
            const d = err.response?.data;
            setError(d?.error ? `${d.error}${d.details ? ` (${d.details})` : ""}` : "Errore nella chiusura. Riprova.");
            setSaving(false);
        }
    };

    return (
        <div className={`modal-overlay${top ? " gp-overlay-top" : ""}`} onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
            <div className="modal" style={{ maxWidth: 560 }}>
                <div className="modal-header">
                    <div className="m-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3>Chiudi percorso</h3>
                        <div className="h-sub">{p.nome || "—"} · {p.customId || "—"} · {formatData(p.data)}</div>
                    </div>
                    <button className="modal-close" onClick={onClose} disabled={saving}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>
                {error && <div className="modal-error">{error}</div>}
                <div className="modal-body">
                    <div className="gp-chiusura-passaggio">
                        <span className={`badge ${statoBadgeClass(p.stato)}`}>{p.stato || "—"}</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                        <span className={`badge ${statoBadgeClass(p.statoProposto)}`}>{p.statoProposto || "—"}</span>
                    </div>
                    <div className="gp-info-section">
                        {/* Riapertura dimenticata (task 532): non è un percorso "mai chiuso", va detto. */}
                        {p.riapertura && (
                            <p className="gp-riaperto-nota">
                                {riaperturaNonRichiusa(p.riapertura)}: il caposquadra l'aveva riaperto per correggerlo.
                                {" "}Chiudendolo ora vale quello che risulta fatto adesso.
                            </p>
                        )}
                        <p>
                            L'operatore ha segnato come fatte <strong>{p.tratteFatte ?? "?"} tratte su {p.tratteTotali}</strong>.
                            {" "}Lo stato finale si decide da queste: tutte fatte → <strong>Completato</strong>, alcune o nessuna
                            {" "}→ <strong>Parziale</strong>; un percorso mai iniziato e senza tratte fatte → <strong>Non eseguito</strong>.
                        </p>
                        <p>
                            Dopo la chiusura il percorso entra nello Storico, nella Reportistica e nelle Tratte da recuperare
                            {" "}(le tratte non fatte prendono la stella).
                        </p>
                        <p><strong>L'operazione avviene su Interacta e non si può annullare.</strong></p>
                    </div>
                </div>
                <div className="modal-footer">
                    <div className="footer-hint">Ritardo: {p.giorniRitardo} {p.giorniRitardo === 1 ? "giorno" : "giorni"}</div>
                    <div className="footer-actions">
                        <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Annulla</button>
                        <button className="btn btn-primary" onClick={conferma} disabled={saving || !p.statoProposto}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                            {saving ? "Chiusura..." : "Chiudi percorso"}
                        </button>
                    </div>
                </div>
                {saving && <div className="gp-save-overlay"><Lf23Loader testo="Chiusura su Interacta..." /></div>}
            </div>
        </div>
    );
}
